import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Snackbar,
    TablePagination,
    TableSortLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { get, post, put, del } from "../../utility/fetch";

export default function RoleSpecialistManager({ embedded = false }) {
    const [specialists, setSpecialists] = useState([]);
    const [roles, setRoles] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ roleId: "", specialistName: "" });
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [orderBy, setOrderBy] = useState("specialistName");
    const [order, setOrder] = useState("asc");

    const fetchSpecialists = async () => {
        const response = await get("/rolespecialist/list");
        setSpecialists(response.data);
    };

    const fetchRoles = async () => {
        const response = await get("/role/list");
        setRoles(response.data);
    };

    const handleSubmit = async () => {
        try {
            if (editMode) {
                await put(`/rolespecialist/${selected.id}`, form);
                setSnackbar({ open: true, message: "Updated successfully" });

            } else {
                await post("/rolespecialist", { roleSpecialists: [form] });
                setSnackbar({ open: true, message: "Created successfully" });
            }
            fetchSpecialists();
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };

    const handleEdit = (item) => {
        setEditMode(true);
        setSelected(item);
        setForm({
            roleId: item.role.id,
            specialistName: item.specialistName,
        });
        setOpenDialog(true);
    };

    const handleDelete = async () => {
        try {
            await del(`/rolespecialist/${deleteConfirm.id}`);
            setSnackbar({ open: true, message: "Deleted successfully" });
            fetchSpecialists();
            setDeleteConfirm(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditMode(false);
        setForm({ roleId: "", specialistName: "" });
        setSelected(null);
    };

    const handleSearch = async () => {
        if (search.trim()) {
            const response = await get(
                `/rolespecialist/filter-list/SpecialistName/${search}`
            );
            setSpecialists(response.data);
        } else {
            fetchSpecialists();
        }
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedData = [...specialists].sort((a, b) => {
        const valueA = a[orderBy]?.toString().toLowerCase() || "";
        const valueB = b[orderBy]?.toString().toLowerCase() || "";
        return (order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA));
    });

    useEffect(() => {
        fetchSpecialists();
        fetchRoles();
    }, []);

    return (
        <Box p={embedded ? 0 : 3}>
            {!embedded && (
                <Typography variant="h5" mb={2}>
                    Role Specialists Management
                </Typography>
            )}

            <Box mb={2} display="flex" gap={2}>
                <TextField
                    label="Search Specialist"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Button variant="outlined" onClick={handleSearch}>
                    Search
                </Button>
                <Button variant="contained" onClick={() => setOpenDialog(true)}>
                    Add Role Specialist
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sortDirection={orderBy === "role.name" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "role.name"}
                                    direction={orderBy === "role.name" ? order : "asc"}
                                    onClick={() => handleSort("role.name")}
                                >
                                    Role
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "specialistName" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "specialistName"}
                                    direction={orderBy === "specialistName" ? order : "asc"}
                                    onClick={() => handleSort("specialistName")}
                                >
                                    Specialist Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.role.name}</TableCell>
                                <TableCell>{item.specialistName}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEdit(item)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setDeleteConfirm(item)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={specialists.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            <Dialog open={openDialog} onClose={handleClose}>
                <DialogTitle>{editMode ? "Edit Role Specialist" : "Add Role Specialist"}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={form.roleId}
                            label="Role"
                            onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Specialist Name"
                        value={form.specialistName}
                        onChange={(e) => setForm({ ...form, specialistName: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editMode ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete specialist "{deleteConfirm?.specialistName}"?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button color="error" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Box
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        boxShadow: 3,
                    }}
                >
                    {snackbar.message}
                </Box>
            </Snackbar>

        </Box>
    );
}
